import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  WritableSignal,
  inject,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { delay, filter, Subscription } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { ElementActions, ElementTypes, GaContext, GaState } from '@fcom/common';
import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import { CmsQueryListItem, CmsTaxonomy } from '@fcom/core-api';
import { ChipTheme } from '@fcom/ui-components/components/chip/interfaces';
import { LocalizationPipe } from '@fcom/ui-translate';

import {
  BluewingsChipsService,
  IgnoreChips,
  type ChipInformation,
} from '../../../services/bluewings-chips/chips.service';

@Component({
  selector: 'fin-chips-filtering',
  templateUrl: './chips-filtering.component.html',
  styleUrl: './chips-filtering.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChipsFilteringComponent implements OnInit, OnDestroy {
  private localization = inject(LocalizationPipe);
  private route = inject(ActivatedRoute);
  private chipsService = inject(BluewingsChipsService);
  private gtmService = inject(GtmService);

  private subscription = new Subscription();
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ChipTheme = ChipTheme;

  chipsList: InputSignal<string[]> = input<string[]>();
  filter: WritableSignal<string | null> = signal(null);
  filterCount: WritableSignal<number> = signal(0);
  filterValue: OutputEmitterRef<string> = output<string>();
  hasBorder: Signal<boolean> = input<boolean>(true);
  hasTitle: Signal<string | null> = input<string>(null);
  items: InputSignal<CmsQueryListItem[]> = input<CmsQueryListItem[]>();
  templateTags: InputSignal<CmsTaxonomy[]> = input<CmsTaxonomy[]>();

  getChips: Signal<ChipInformation[]> = computed<ChipInformation[]>((): ChipInformation[] => {
    const tags: string[] = [];
    const chips: ChipInformation[] = [];

    if (this.chipsList()) {
      this.chipsList().forEach((chip): number => tags.push(chip));
    } else {
      this.items().forEach((item: CmsQueryListItem): void =>
        item.subjectTaxonomyTags.forEach((tag): void => {
          tags.push(tag);
        })
      );
    }

    const newTagSet: string[] = Array.from(new Set(tags));

    newTagSet.forEach((chip: string): number =>
      chips.push({ label: chip, text: this.localization.transform('blueWingsSEO.tags.' + chip) })
    );

    return chips.sort((a, b): number => a.text.localeCompare(b.text));
  });
  chips: Signal<ChipInformation[]> = computed<ChipInformation[]>((): ChipInformation[] => {
    const chipsToIgnore: string[] = [IgnoreChips.DESTINATIONS, IgnoreChips.WORLD_OF_FINNAIR];
    if (this.templateTags()) {
      this.templateTags().forEach((tag: CmsTaxonomy): number => chipsToIgnore.push(tag));
    }
    return this.getChips().filter((chip: ChipInformation): boolean => !chipsToIgnore.includes(chip.label));
  });

  ngOnInit(): void {
    this.subscription.add(
      this.route.queryParams.pipe(filter(Boolean), delay(0)).subscribe((params): void => {
        if (!params.filter) {
          return;
        }

        let count = 0;
        this.chipsService.filter$.next(params.filter);
        this.filterValue.emit(params.filter);
        if (this.items()) {
          this.items().forEach((item: CmsQueryListItem): void => {
            item.subjectTaxonomyTags.forEach((tag: CmsTaxonomy): number =>
              tag === params.filter ? (count += 1) : null
            );
          });
        }
        return this.filterCount.set(count);
      })
    );

    this.subscription.add(
      this.chipsService.filter$.subscribe((value): void => {
        this.filter.set(value);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  toggleContent(chip: string): void {
    let count = 0;

    // removes filter & resets count
    if (this.filter() === chip) {
      this.trackElement(`cluster-filter-${this.filter()}`, GaState.REMOVE);
      this.chipsService.filter$.next(null);
      this.filterValue.emit(null);
      this.filterCount.set(0);
      return;
    }

    this.chipsService.filter$.next(chip);
    this.filterValue.emit(chip);
    this.trackElement(`cluster-filter-${this.filter()}`, GaState.ADD);

    if (this.chipsList()) {
      return;
    }

    this.items().forEach((item: CmsQueryListItem): void => {
      item.subjectTaxonomyTags.forEach((tag: CmsTaxonomy): number => (tag === chip ? (count += 1) : null));
    });
    this.filterCount.set(count);
  }

  private trackElement(label: string, state: string): void {
    this.gtmService.trackElement(label, GaContext.CONTENT, ElementTypes.BUTTON, state, ElementActions.CLICK);
  }
}
